import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LuSearch, LuX } from 'react-icons/lu'

import { useEscapeKey } from '@/lib/core/hooks/useEscapeKey'
import { Badge, BadgeVariant } from '@/lib/core/components/Badge'
import { TooltipContainerProvider } from '@/lib/core/components/Tooltip'
import { Truncate } from '@/lib/core/components/Truncate'

import { PopoverPosition, usePopoverPositioning } from '../../hooks'
import { AssetTag } from '../TableHeaderTags'
import styles from './TagsPopoutView.module.css'

export interface TagsPopoutViewProps {
  popoverPosition: PopoverPosition | null
  closeTagsPopout: () => void
  isTagsPopoutOpen: boolean
  tagsForSelection: AssetTag[]
  storedTags: AssetTag[]
  setStoredTags: (tags: AssetTag[]) => void
}

export const TagsPopoutView = ({
  popoverPosition,
  closeTagsPopout,
  isTagsPopoutOpen,
  tagsForSelection,
  storedTags,
  setStoredTags,
}: TagsPopoutViewProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [popoverDimensions, setPopoverDimensions] = useState({ width: 500, height: 400 })
  const { applyPosition } = usePopoverPositioning(popoverDimensions, { margin: 10 })

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTags, setSelectedTags] = useState<AssetTag[]>([])

  // Initialize selectedTags from store when popout opens
  useEffect(() => {
    if (isTagsPopoutOpen) {
      setSelectedTags(storedTags)
    }
  }, [isTagsPopoutOpen, storedTags])

  // Clear search term when popout closes
  useEffect(() => {
    if (!isTagsPopoutOpen) {
      setSearchTerm('')
    }
  }, [isTagsPopoutOpen])

  // Filter tags based on search
  const filteredTags = useMemo(() => {
    return tagsForSelection.filter((tag) =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [tagsForSelection, searchTerm])

  // Position the popover
  const positionPopover = useCallback(() => {
    if (!dialogRef.current || !popoverPosition) return

    const { offsetWidth, offsetHeight } = dialogRef.current

    if (offsetWidth !== popoverDimensions.width || offsetHeight !== popoverDimensions.height) {
      setPopoverDimensions({ width: offsetWidth, height: offsetHeight })
    }

    applyPosition(dialogRef.current, popoverPosition, {
      width: offsetWidth,
      height: offsetHeight,
    })
  }, [popoverPosition, applyPosition, popoverDimensions])

  const handleClose = useCallback(() => {
    if (dialogRef.current) {
      dialogRef.current.hidePopover()
    }
    closeTagsPopout()
  }, [closeTagsPopout])

  const handleTagToggle = useCallback(
    (tag: AssetTag, e?: React.MouseEvent | React.ChangeEvent) => {
      e?.stopPropagation()
      setSelectedTags((prev) => {
        const isSelected = prev.some((selectedTag) => selectedTag.id === tag.id)
        if (isSelected) {
          return prev.filter((selectedTag) => selectedTag.id !== tag.id)
        } else {
          return [...prev, tag]
        }
      })
    },
    [filteredTags],
  )

  const handleClearAll = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation()
      setSelectedTags([])
      setStoredTags([])
    },
    [setStoredTags],
  )

  const handleApply = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation()
      setStoredTags(selectedTags)
      handleClose()
    },
    [selectedTags, setStoredTags, handleClose],
  )

  // All this logic is for handling the clicks outside the popover to close it
  // Track where the mouse was initially pressed to prevent closing on drag-release
  const mouseDownTargetRef = useRef<EventTarget | null>(null)
  // Handle mouse down to track where the interaction started
  const handleDocumentMouseDown = useCallback((e: MouseEvent) => {
    mouseDownTargetRef.current = e.target
  }, [])
  // Create a stable reference to the click handler
  const handleDocumentClick = useCallback(
    (e: MouseEvent) => {
      // Only close if both mousedown and mouseup/click happened outside the popover
      const clickTarget = e.target as Node
      const mouseDownTarget = mouseDownTargetRef.current as Node

      if (
        dialogRef.current &&
        !dialogRef.current.contains(clickTarget) &&
        mouseDownTarget &&
        !dialogRef.current.contains(mouseDownTarget)
      ) {
        closeTagsPopout()
      }

      // Reset the mousedown target
      mouseDownTargetRef.current = null
    },
    [closeTagsPopout],
  )

  // Add event listener for click-outside behavior
  useEffect(() => {
    if (!isTagsPopoutOpen) return

    // Add event listeners for mouse interactions on the document
    document.addEventListener('mousedown', handleDocumentMouseDown)
    document.addEventListener('click', handleDocumentClick)

    // Clean up the event listeners
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [isTagsPopoutOpen, closeTagsPopout, handleDocumentClick, handleDocumentMouseDown])

  // Add event listener for the popover closing via native popover API
  useEffect(() => {
    const handlePopoverClose = () => {
      // Check if the popover is still open
      if (dialogRef.current && !dialogRef.current.matches(':popover-open')) {
        closeTagsPopout()
      }
    }

    // Add event listener to the dialog element for toggle events
    const dialogElement = dialogRef.current
    if (dialogElement) {
      dialogElement.addEventListener('toggle', handlePopoverClose)
    }

    // Clean up the event listeners
    return () => {
      if (dialogElement) {
        dialogElement.removeEventListener('toggle', handlePopoverClose)
      }
    }
  }, [closeTagsPopout])

  useEscapeKey(isTagsPopoutOpen, closeTagsPopout)

  // Prevent body scrolling when popout is open while keeping scrollbar visible
  useEffect(() => {
    if (isTagsPopoutOpen) {
      // Store the current scroll position and styles
      const scrollY = window.scrollY
      const originalPosition = document.body.style.position
      const originalTop = document.body.style.top
      const originalWidth = document.body.style.width
      const originalOverflow = document.body.style.overflow

      // Fix the body position to prevent scrolling while keeping scrollbar
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        // Restore the original styles and scroll position
        document.body.style.position = originalPosition
        document.body.style.top = originalTop
        document.body.style.width = originalWidth
        document.body.style.overflow = originalOverflow

        // Restore scroll position
        window.scrollTo(0, scrollY)
      }
    }
  }, [isTagsPopoutOpen])

  useEffect(() => {
    if (isTagsPopoutOpen && dialogRef.current) {
      positionPopover()
      dialogRef.current.showPopover()
    } else if (dialogRef.current) {
      dialogRef.current.hidePopover()
    }
  }, [isTagsPopoutOpen, positionPopover])

  if (!isTagsPopoutOpen) {
    return null
  }

  return (
    <dialog ref={dialogRef} className={styles.popoutContainer} popover="auto">
      <TooltipContainerProvider container={dialogRef.current}>
        <div className={styles.popoutHeader}>
          <div className={styles.searchContainer}>
            <LuSearch size={16} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.headerActions}>
            <button className={styles.actionButton} onClick={handleClose}>
              <LuX size={16} />
            </button>
          </div>
        </div>

        <div className={styles.tagsList}>
          {filteredTags.length === 0 ? (
            <div className={styles.emptyState}>
              {searchTerm ? 'No tags match your search' : 'No tags available'}
            </div>
          ) : (
            filteredTags.map((tag: AssetTag) => {
              const isSelected = selectedTags.some((selectedTag) => selectedTag.id === tag.id)
              return (
                <div
                  key={tag.id}
                  className={`${styles.tagItem} ${isSelected ? styles.selected : ''}`}
                  onClick={(e) => handleTagToggle(tag, e)}
                >
                  <div className={styles.leftSide}>
                    <div className={styles.tagCheckbox}>
                      <input
                        type="checkbox"
                        id={`filter-tag-${tag.id}`}
                        checked={isSelected}
                        onChange={(e) => handleTagToggle(tag, e)}
                      />
                      <label
                        htmlFor={`filter-tag-${tag.id}`}
                        className={styles.checkboxLabel}
                        style={{
                          backgroundColor: isSelected ? '#314663' : 'transparent',
                          borderColor: isSelected ? '#314663' : '#ccc',
                        }}
                      >
                        {isSelected && <span className={styles.checkmark}>✓</span>}
                      </label>
                    </div>
                    <Badge variant={BadgeVariant.TAG} backgroundColor={tag.color} minSize={20}>
                      <Truncate content={tag.name} limit={10} />
                    </Badge>
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className={styles.footerActions}>
          <button className={styles.clearAllButton} onClick={handleClearAll}>
            Clear all
          </button>
          <button className={styles.applyButton} onClick={handleApply}>
            Apply
          </button>
        </div>
      </TooltipContainerProvider>
    </dialog>
  )
}
